import { RESERVED_USER_CREATE_ID } from "@/constants/util.constants";
import {
  ConvertedOrderItem,
  ConvertedQuoteItem,
  DBBranchesByCompanyID,
  EditableCompany,
  FullSingleOrder,
  FullSingleQuote,
  UserAdminUpdateProfileDetails,
} from "@/types";

export async function convertOrderItem(
  singleOrder: FullSingleOrder
): Promise<ConvertedOrderItem[]> {
  return singleOrder.order_items.map((item) => ({
    ...item,
    delivery_note_issued: singleOrder.delivery_note_issued,
    delivery_note_proof_of_delivery:
      singleOrder.delivery_note_proof_of_delivery,
    nc_number: singleOrder.nc_number,
    assigned_to: singleOrder.assigned_to,
  }));
}

export async function convertQuoteItem(
  singleQuote: FullSingleQuote
): Promise<ConvertedQuoteItem[]> {
  return singleQuote.quote_items.map((item) => ({
    ...item,
    line_no: singleQuote.line_no,
  }));
}

type EditableCompanyOptionalIDS = {
  id?: string;
  accountNumber: string;
  type: "business" | "private";
  name: string;
  registrationNo?: string | undefined;
  vatNo?: string | undefined;
  industryType: string;
  contactNumber: string;
  branches: EditableBranchOptionalIDS[];
};

type EditableBranchOptionalIDS = {
  id?: string;
  name: string;
  addressLine1: string;
  addressLine2?: string;
  suburb?: string;
  city: string;
  province: string;
  country: string;
  officeParkName?: string;
  postalCode: string;
  latitude: number;
  longitude: number;
};

export async function removeNewCompaniesOrBranchesIDs(
  companies: EditableCompany[]
): Promise<EditableCompanyOptionalIDS[]> {
  // We must remove (set to undefined) company_id and branch_id if they are newly created,
  // so that only existing IDs are sent to the backend.
  // New companies or branches will be created by the backend
  const newCompanies: EditableCompanyOptionalIDS[] = companies.map(
    (company) => ({
      ...company,
      id: company.id.includes(RESERVED_USER_CREATE_ID) ? undefined : company.id,
      accountNumber: company.accountNo || "",
      branches: company.branches.map((branch) => ({
        ...branch,
        id: branch.id.includes(RESERVED_USER_CREATE_ID) ? undefined : branch.id,
      })),
    })
  );

  return newCompanies;
}

type DBUserCompanyUpdate = {
  company_name: string;
  company_type: "business" | "private";
  industry_type: string;
  company_id?: string | undefined;
  registration_number?: string | undefined;
  tax_number?: string | undefined;
  phone_number?: string | undefined;
  branch?:
    | {
        branch_name: string;
        branch_address1: string;
        branch_city: string;
        branch_pin_code: string;
        branch_province: string;
        branch_country: string;
        branch_latitude: string;
        branch_longitude: string;
        branch_id?: string | undefined;
        branch_office_park_name?: string | undefined;
        branch_address2?: string | undefined;
        branch_suburb?: string | undefined;
      }[]
    | undefined;
};

export async function convertEditableCompanyToDBExpectedFormat(
  companies: EditableCompany[]
): Promise<DBUserCompanyUpdate[]> {
  const convertedCompanies = await removeNewCompaniesOrBranchesIDs(companies);
  return convertedCompanies.map((company) => ({
    company_id: company.id,
    company_type: company.type,
    company_name: company.name,
    account_number: company.accountNumber || "",
    registration_number: company.registrationNo || undefined,
    tax_number: company.vatNo || undefined,
    industry_type: company.industryType,
    phone_number: company.contactNumber,
    branch: company.branches.map((branch) => ({
      branch_id: branch.id,
      branch_name: branch.name,
      branch_address1: branch.addressLine1,
      branch_address2: branch.addressLine2 || undefined,
      branch_suburb: branch.suburb || undefined,
      branch_city: branch.city,
      branch_province: branch.province,
      branch_country: branch.country,
      branch_office_park_name: branch.officeParkName || undefined,
      branch_pin_code: branch.postalCode,
      branch_latitude: branch.latitude.toString(),
      branch_longitude: branch.longitude.toString(),
    })),
  }));
}

type DBBranchesByCompanyIDOptionalID = {
  company_id?: string;
  account_number: string;
  company_type: "business" | "private";
  company_name: string;
  registration_number: string | null;
  tax_number: string | null;
  industry_type: string;
  phone_number: string;
  branch: {
    branch_id?: string;
    branch_name: string;
    branch_address1: string;
    branch_address2: string | undefined;
    branch_suburb: string | undefined;
    branch_city: string;
    branch_province: string;
    branch_country: string;
    branch_office_park_name: string | undefined;
    branch_pin_code: string;
    branch_latitude: string;
    branch_longitude: string;
  }[];
};

export async function removeNewCompaniesOrBranchesIDsUserAdminUpdate(
  companies: DBBranchesByCompanyID[]
): Promise<DBBranchesByCompanyIDOptionalID[]> {
  // We must remove (set to undefined) company_id and branch_id if they are newly created,
  // so that only existing IDs are sent to the backend.
  // New companies or branches will be created by the backend
  const newCompanies: DBBranchesByCompanyIDOptionalID[] = companies.map(
    (company) => ({
      ...company,

      branches: undefined,
      company_id: company.company_id.includes(RESERVED_USER_CREATE_ID)
        ? undefined
        : company.company_id,
      account_number: company.account_number || "",
      branch: company.branches.map((branch) => ({
        ...branch,
        branch_address2: branch.branch_address2 || undefined,
        branch_suburb: branch.branch_suburb || undefined,
        branch_province: branch.branch_province || "",
        branch_office_park_name: branch.branch_office_park_name || undefined,
        branch_id: branch.branch_id.includes(RESERVED_USER_CREATE_ID)
          ? undefined
          : branch.branch_id,
      })),
    })
  );
  return newCompanies;
}

type DB_UserAdminUpdateProfileDetails = {
  userID: string;
  email: string;
  title: string | null;
  fullName: string;
  phone_number: string;
  preferredLanguage: string;
  preferredCommunicationMethods: string[];
  designation: string;
  marketingOptIn: boolean;
  company: DBBranchesByCompanyIDOptionalID[];
};

export async function convertUserDetailsToDBExpectedFormat(
  userDetails: UserAdminUpdateProfileDetails
): Promise<DB_UserAdminUpdateProfileDetails> {
  const transformedUserDetails: DB_UserAdminUpdateProfileDetails = {
    userID: userDetails.id,
    email: userDetails.email,
    title: userDetails.title || null,
    fullName: userDetails.fullName,
    phone_number: userDetails.phone_number,
    preferredLanguage: userDetails.preferredLanguage,
    preferredCommunicationMethods: userDetails.preferredCommunicationMethods,
    designation: userDetails.designation,
    marketingOptIn: userDetails.marketingOptIn,
    company: await removeNewCompaniesOrBranchesIDsUserAdminUpdate(
      userDetails.companies
    ),
  };

  return transformedUserDetails;
}
